/**
 * TypeScript types for document management
 * Matches the backend schemas and models
 */

export interface DocumentCollection {
  id: string;
  name: string;
  description?: string;
  parent_collection_id?: string;
  collection_type: CollectionType;
  entity_type?: string;
  entity_id?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by?: string;

  // Computed fields
  item_count?: number;
  path?: string;

  // Related data
  parent?: DocumentCollection;
  children?: DocumentCollection[];
}

export interface Document {
  id: string;
  name: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  checksum: string;
  storage_path: string;
  storage_provider: StorageProvider;
  status: DocumentStatus;
  version: number;
  tags: string[];
  document_types?: DocumentType[];
  custom_metadata?: Record<string, unknown>;
  extracted_text?: string;
  uploaded_by: string;
  upload_date: string;
  as_of?: string | null;
  /** as_of, else the upload day in UTC; what the server sorts by. */
  effective_as_of: string;
  created_at: string;
  updated_at?: string;

  // Computed fields
  download_url?: string;
  file_size_display?: string;

  // Whether the document is exposed in the investor portal. This is a
  // document-level master switch; per-fund scoping is expressed separately
  // via a FUND sibling entity association.
  portal_visible?: boolean;

  // Relations
  collections?: DocumentCollection[];
  entity_associations?: DocumentEntityAssociation[];
}

interface DocumentEntityAssociation {
  id: string;
  document_id: string;
  entity_type: string;
  entity_id: string;
  association_role: string;
  context_type?: string;
  context_name?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  created_by: string;

  // Computed fields
  entity_name?: string;
}

// Enums
export enum EntityType {
  FUND = 'FUND',
  INVESTOR = 'INVESTOR',
  PORTFOLIO_COMPANY = 'PORTFOLIO_COMPANY',
  CONVERTIBLE_INSTRUMENT = 'CONVERTIBLE_INSTRUMENT',
}

/** The folder an investor document sits in under its fund. Stored as the
 *  FUND association's context_type; none means a loose file. */
export enum FundDocumentContextType {
  CAPITAL_CALL_NOTICE = 'capital_call_notice',
  DISTRIBUTION_NOTICE = 'distribution_notice',
  CAPITAL_ACCOUNT_STATEMENT = 'capital_account_statement',
}

export interface InvestorFundFolder {
  fund_id: string;
  fund_name: string;
}

/** fund_id null means the General folder. */
export interface InvestorFolderDocument {
  document: Document;
  fund_id: string | null;
  context_type: FundDocumentContextType | null;
}

export interface InvestorDocumentFoldersResponse {
  funds: InvestorFundFolder[];
  documents: InvestorFolderDocument[];
}

export enum CollectionType {
  FOLDER = 'FOLDER',
  ENTITY_FOLDER = 'ENTITY_FOLDER',
  SMART_FOLDER = 'SMART_FOLDER',
  TEMPLATE = 'TEMPLATE',
}

export enum DocumentStatus {
  UPLOADING = 'UPLOADING',
  PROCESSING = 'PROCESSING',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

export enum StorageProvider {
  LOCAL = 'LOCAL',
  S3 = 'S3',
}

export enum DocumentSource {
  UPLOADED = 'uploaded',
  LEGAL = 'legal',
  BANK = 'bank',
}

export enum DocumentType {
  SHA = 'SHA',
  SPA = 'SPA',
  CLA = 'CLA',
  DEED_OF_ISSUANCE = 'DEED_OF_ISSUANCE',
  CONVERSION_NOTICE = 'CONVERSION_NOTICE',
  BOARD_RESOLUTION = 'BOARD_RESOLUTION',
  SHAREHOLDER_RESOLUTION = 'SHAREHOLDER_RESOLUTION',
  DEED_OF_CANCELLATION = 'DEED_OF_CANCELLATION',
  TERMSHEET = 'TERMSHEET',
  WARRANT_AGREEMENT = 'WARRANT_AGREEMENT',
  SIDE_LETTER = 'SIDE_LETTER',
  INVOICE = 'INVOICE',
  TRANSFER_AGREEMENT = 'TRANSFER_AGREEMENT',
  OTHER = 'OTHER',
}

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  [DocumentType.SHA]: 'Shareholders Agreement (SHA)',
  [DocumentType.SPA]: 'Share Purchase Agreement (SPA)',
  [DocumentType.CLA]: 'Convertible Loan Agreement (CLA)',
  [DocumentType.DEED_OF_ISSUANCE]: 'Deed of issuance',
  [DocumentType.CONVERSION_NOTICE]: 'Conversion notice',
  [DocumentType.BOARD_RESOLUTION]: 'Board resolution',
  [DocumentType.SHAREHOLDER_RESOLUTION]: 'Shareholder resolution (AVA)',
  [DocumentType.DEED_OF_CANCELLATION]: 'Deed of cancellation',
  [DocumentType.TERMSHEET]: 'Term sheet',
  [DocumentType.WARRANT_AGREEMENT]: 'Warrant agreement',
  [DocumentType.SIDE_LETTER]: 'Side letter',
  [DocumentType.INVOICE]: 'Invoice',
  [DocumentType.TRANSFER_AGREEMENT]: 'Transfer agreement',
  [DocumentType.OTHER]: 'Other',
};

// API Request/Response Types
export interface CreateCollectionRequest {
  name: string;
  description?: string;
  parent_collection_id?: string;
  collection_type?: CollectionType;
  entity_type?: string;
  entity_id?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateCollectionRequest {
  name?: string;
  description?: string;
  parent_collection_id?: string;
  metadata?: Record<string, unknown>;
}

export interface UploadDocumentRequest {
  file: File;
  collection_id?: string;
  document_source?: DocumentSource;
  document_types?: DocumentType[];
  metadata?: Record<string, unknown>;
  tags?: string[];
  entity_associations?: {
    entity_type: string;
    entity_id: string;
    role: string;
    context_type?: string;
    context_name?: string;
  }[];
}

// Upload a document scoped to a single entity (fund, investor, portfolio
// company). The who-tag is derived server-side from entityType/entityId, and
// portal visibility is a document-level flag rather than a per-association one.
export interface UploadEntityDocumentRequest {
  file: File;
  collection_id: string;
  filename?: string;
  document_source?: DocumentSource;
  document_types?: DocumentType[];
  metadata?: Record<string, unknown>;
  tags?: string[];
  portal_visible?: boolean;
  fund_id?: string;
  context_type?: FundDocumentContextType;
  as_of?: string;
}

export interface DocumentUploadResponse {
  document?: Document;
  message: string;
}

export interface DocumentSearchRequest {
  query?: string;
  tags?: string[];
  entity_type?: string;
  entity_id?: string;
  collection_id?: string;
  content_type?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface DocumentSearchResponse {
  documents: Document[];
  total: number;
  offset: number;
  limit: number;
}

export interface CollectionAncestor {
  id: string;
  name: string;
}

export interface CollectionContentsResponse {
  collection: DocumentCollection;
  ancestors: CollectionAncestor[];
  subcollections: DocumentCollection[];
  documents: Document[];
}

export interface DocumentUploadProgress {
  file: File;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  document?: Document;
}

export interface DocumentFilters {
  searchTerm: string;
  entity_type?: string;
  entity_id?: string;
  content_type?: string;
  tags?: string[];
  date_from?: string;
  date_to?: string;
}

// Constants
const DOCUMENT_MIME_TYPES = {
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'PowerPoint',
  'text/plain': 'Text',
  'text/csv': 'CSV',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/svg+xml': 'SVG',
  'application/zip': 'ZIP',
  'application/x-zip-compressed': 'ZIP',
};

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const SUPPORTED_FILE_TYPES = Object.keys(DOCUMENT_MIME_TYPES);
